export interface ApiClientOptions {
  apiBase: string
  getIdToken: () => Promise<string | null>
}

export class ApiError extends Error {
  status: number
  body: unknown
  /** Human-readable reason pulled from the response body, when present. */
  detail: string | null
  constructor(status: number, body: unknown) {
    const detail = extractDetail(body)
    super(detail ? `HTTP ${status} — ${detail}` : `HTTP ${status}`)
    this.status = status
    this.body = body
    this.detail = detail
  }
}

/** Pull the most useful message out of a parsed error body. */
function extractDetail(body: unknown): string | null {
  if (typeof body === "string") return body.trim() || null
  if (body && typeof body === "object") {
    const b = body as Record<string, unknown>
    const v = b.detail ?? b.error ?? b.message
    if (typeof v === "string" && v.trim()) return v.trim()
  }
  return null
}

export class ApiClient {
  constructor(private readonly opts: ApiClientOptions) {}

  async get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path)
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("POST", path, body)
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>("PUT", path, body)
  }

  async delete<T>(path: string): Promise<T> {
    return this.request<T>("DELETE", path)
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await this.opts.getIdToken()
    const headers: Record<string, string> = { "Content-Type": "application/json" }
    if (token) headers.Authorization = `Bearer ${token}`
    const url = `${this.opts.apiBase}${path}`
    const r = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
    const text = await r.text()
    let parsed: unknown = null
    try { parsed = text ? JSON.parse(text) : null } catch { parsed = text }
    if (!r.ok) {
      throw new ApiError(r.status, parsed)
    }
    return parsed as T
  }
}

export function buildClientFromWindowConfig(getIdToken: () => Promise<string | null>): ApiClient {
  // In local dev the prod API rejects CORS from localhost, so route calls through
  // the Vite dev proxy (/__api → api.eyesight.q-say.com, server-side, no CORS).
  const apiBase = import.meta.env.DEV ? "/__api" : window.__EYESIGHT_CONFIG__.apiBase
  return new ApiClient({
    apiBase,
    getIdToken,
  })
}
